/**
 * Admin Role Utilities and Hooks
 * Provides role-based access control helpers for the frontend
 */

// Get admin role from sessionStorage (SECURITY: Admin uses sessionStorage)
export const getAdminRole = () => {
  return sessionStorage.getItem('adminRole') || 'staff';
};

// Get current admin's email (used for self-* guards on the Staff Management screen).
export const getAdminEmail = () => {
  try {
    const raw = sessionStorage.getItem('admin');
    if (!raw) return null;
    return (JSON.parse(raw).email || '').toLowerCase() || null;
  } catch {
    return null;
  }
};

// Check if admin has a specific role
export const hasRole = (requiredRole) => {
  const currentRole = getAdminRole();
  const roleHierarchy = { admin: 3, manager: 2, staff: 1 };
  return roleHierarchy[currentRole] >= roleHierarchy[requiredRole];
};

// Check if admin is an admin (highest level)
export const isAdmin = () => {
  return getAdminRole() === 'admin';
};

// Check if admin is a manager or higher
export const isManagerOrAbove = () => {
  const role = getAdminRole();
  return role === 'admin' || role === 'manager';
};

// Permission matrix - matches backend /app/backend/middleware/rbac.py::PERMISSIONS
// Order of resources intentionally mirrors that dict for 1:1 review.
export const canAccess = (resource) => {
  const role = getAdminRole();

  const permissions = {
    admin: [
      'dashboard', 'bookings', 'customers', 'staff',
      'pricing', 'locations', 'analytics', 'settings', 'security', 'integrations',
      'manual_booking', 'db_tools', 'site_banner', 'holidays',
    ],
    manager: [
      'dashboard', 'bookings', 'customers', 'staff',
      'pricing', 'locations', 'analytics',
      'manual_booking', 'site_banner', 'holidays',
      // No: settings, security, integrations, db_tools
    ],
    staff: [
      'dashboard', 'bookings', 'customers',
      'pricing', 'locations',
      'manual_booking', 'site_banner', 'holidays',
      // No: staff, analytics, settings, security, integrations, db_tools
    ],
  };

  return permissions[role]?.includes(resource) || false;
};

// Check if user can edit/write to a resource
export const canEdit = (resource) => {
  const role = getAdminRole();

  const editPermissions = {
    admin: [
      'dashboard', 'bookings', 'customers', 'staff',
      'pricing', 'locations', 'analytics', 'settings', 'security', 'integrations',
      'manual_booking', 'db_tools', 'site_banner', 'holidays',
    ],
    manager: [
      'bookings', 'customers',
      'staff',                                    // Upgraded: Manager can CRUD other managers + staff
      'pricing', 'locations',                    // Upgraded: Manager now edits pricing + locations
      'analytics',
      'manual_booking', 'site_banner', 'holidays',
      // Read-only: dashboard
    ],
    staff: [
      'bookings',
      'manual_booking', 'site_banner', 'holidays',
      // Read-only: customers, pricing, locations, dashboard
    ],
  };

  return editPermissions[role]?.includes(resource) || false;
};

// Check if resource is view-only for current role
export const isViewOnly = (resource) => {
  return canAccess(resource) && !canEdit(resource);
};

// Get role display name
export const getRoleDisplayName = (role) => {
  const names = {
    admin: 'Administrator',
    manager: 'Manager',
    staff: 'Staff'
  };
  return names[role] || role;
};

// Get role badge color
export const getRoleBadgeColor = (role) => {
  const colors = {
    admin: 'bg-red-100 text-red-800',
    manager: 'bg-blue-100 text-blue-800',
    staff: 'bg-gray-100 text-gray-800'
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
};
